'use client';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import { crsBugsnag } from 'helpers/bugsnag';
import { memo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AntIcon, ChartBuilderEndDate, ChartBuilderStartDate, SimpleLineIcon } from 'components';
import { usePathname } from 'next/navigation';
import { fetchDynamicChart } from 'services/api/fetchDynamicChart';

const DISPLAY_OPTIONS = {
  INDICATOR_ONLY: 'indicator_only',
  MARKET_ONLY: 'market_only',
  BOTH: 'both',
} as const;

type DisplayOption = (typeof DISPLAY_OPTIONS)[keyof typeof DISPLAY_OPTIONS];

function getRenderValueAsDate(val: string | Date | null) {
  if (val) {
    return new Date(
      new Date(val).toLocaleString('en-US', {
        timeZone: 'UTC',
      })
    );
  }

  return null;
}

function ChartBuilderMenu({ onUpdate }: { onUpdate: (string: any) => void }) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, isValid, errors },
  } = useForm({
    defaultValues: {
      title: '',
      start_date: '',
      end_date: '',
      display_option: DISPLAY_OPTIONS.BOTH,
      show_stats: true,
      show_signals: true,
    },
  });

  const maxStartDate = watch('end_date');
  const minEndDate = watch('start_date');
  const pathname = usePathname();
  const filename = pathname.split('/').pop() ?? '';
  const chartCode = filename.split('.')[0];
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    if (!!searchParams.toString()) {
      const formDataArray = Array.from(searchParams).map(([key, value]) => {
        if (key === 'start_date' || key === 'end_date') {
          return {
            [key]: getRenderValueAsDate(value),
          };
        }
        if (key === 'show_indicator' || key === 'show_market') {
          const showIndicator = searchParams.get('show_indicator') === 'true';
          const showMarket = searchParams.get('show_market') === 'true';
          let displayOption: DisplayOption = DISPLAY_OPTIONS.BOTH;
          if (showIndicator && !showMarket) displayOption = DISPLAY_OPTIONS.INDICATOR_ONLY;
          if (!showIndicator && showMarket) displayOption = DISPLAY_OPTIONS.MARKET_ONLY;
          return { display_option: displayOption };
        }
        return {
          [key]: value === 'true' ? true : value === 'false' ? false : value,
        };
      });

      const formData = Object.assign({}, ...formDataArray);

      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: any, close: () => void) => {
    const { display_option, ...rest } = data;

    // Extract the path that follows 'custom-research/' and remove the filename
    const customResearchPath = pathname.includes('/custom-research/')
      ? pathname.split('/custom-research/')[1]
      : '';
    const dirPath = customResearchPath
      ? customResearchPath.substring(0, customResearchPath.lastIndexOf('/'))
      : '';

    const formData = {
      ...rest,
      start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : '',
      end_date: data.end_date ? dayjs(data.end_date).format('YYYY-MM-DD') : '',
      show_indicator: display_option !== DISPLAY_OPTIONS.MARKET_ONLY,
      show_market: display_option !== DISPLAY_OPTIONS.INDICATOR_ONLY,
      dir: dirPath,
    };

    try {
      const queryParams = new URLSearchParams({
        chart_code: chartCode,
        ...formData,
      });

      const dynamicChartResponse = await fetchDynamicChart(
        `/custom-research/chart?${queryParams.toString()}`
      );

      // Chart builder expects PDF URLs for the viewer
      if (dynamicChartResponse.type === 'pdf') {
        onUpdate(dynamicChartResponse.url);
      } else {
        // HTML responses aren't supported in the chart builder context
        throw new Error('Chart builder only supports PDF responses');
      }

      const urlParams = new URLSearchParams(formData);
      const newUrl = pathname + `?${urlParams.toString()}`;

      // router causes undesirable page refresh--no shallow routing in app router apparently. cool
      // router.push(`?${urlParams.toString()}`);

      window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);

      close();
    } catch (error) {
      setError(`Error: ${(error as Error).message}` || 'Failed to update chart');
      crsBugsnag.notify(error as Error, (event) => {
        event.context = 'Chart Builder Menu';
        event.addMetadata('Form Data', {
          ...formData,
        });
      });
    }
  };

  return (
    <Popover className='relative'>
      <PopoverButton className='inline-flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 focus:ring-teal-500 px-2 py-0.25'>
        <SimpleLineIcon name='settings' className='!text-lg !lg:text-xl  font-normal' />
      </PopoverButton>
      <Transition
        leave='transition ease-in duration-150'
        leaveFrom='opacity-100'
        leaveTo='opacity-0 translate-y-1'>
        <PopoverPanel className='absolute left-1/2 -translate-x-1/2'>
          {({ close }) => (
            <div className='w-[480px] shrink rounded-xl bg-white p-4 text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5'>
              <form onSubmit={handleSubmit((data) => onSubmit(data, close))}>
                <label
                  htmlFor='title'
                  className='block text-sm font-medium text-gray-700 uppercase mb-2'>
                  Chart Title
                </label>
                <input
                  {...register('title')}
                  id='title'
                  placeholder='Chart Title'
                  className='font-normal text-sm text-gray-900 w-full border border-solid border-neutral-200 py-1 px-4 rounded-full mb-8'
                />
                <div className='border-y border-solid border-gray-300 py-6'>
                  <div className='flex gap-4 mb-6'>
                    <div className='w-1/2'>
                      <label
                        htmlFor='start_date'
                        className='block text-sm font-medium text-gray-700 uppercase mb-1'>
                        Start Date
                      </label>
                      <ChartBuilderStartDate control={control} maxDate={maxStartDate} />
                    </div>
                    <div className='w-1/2'>
                      <label
                        htmlFor='end_date'
                        className='block text-sm font-medium text-gray-700 uppercase mb-1'>
                        End Date
                      </label>
                      <ChartBuilderEndDate control={control} minDate={minEndDate} />
                      {errors?.end_date && (
                        <p className='text-red-700 text-sm font-medium'>
                          {errors.end_date.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='flex flex-col gap-4'>
                    <div className='space-y-2'>
                      <p className='text-sm font-medium text-gray-700 uppercase'>Display Options</p>
                      <div className='flex gap-6'>
                        <label className='flex items-center'>
                          <input
                            {...register('display_option')}
                            type='radio'
                            value={DISPLAY_OPTIONS.INDICATOR_ONLY}
                            className='h-4 w-4 mr-2 bg-transparent border-teal-800 text-teal-800 focus:ring-teal-500 cursor-pointer'
                          />
                          <span className='text-sm font-medium text-gray-700 cursor-pointer'>
                            Show Indicator
                          </span>
                        </label>

                        <label className='flex items-center'>
                          <input
                            {...register('display_option')}
                            type='radio'
                            value={DISPLAY_OPTIONS.MARKET_ONLY}
                            className='h-4 w-4 mr-2 bg-transparent border-teal-800 text-teal-800 focus:ring-teal-500 cursor-pointer'
                          />
                          <span className='text-sm font-medium text-gray-700 cursor-pointer'>
                            Show Market
                          </span>
                        </label>

                        <label className='flex items-center'>
                          <input
                            {...register('display_option')}
                            type='radio'
                            value={DISPLAY_OPTIONS.BOTH}
                            className='h-4 w-4 mr-2 bg-transparent border-teal-800 text-teal-800 focus:ring-teal-500 cursor-pointer'
                          />
                          <span className='text-sm font-medium text-gray-700 cursor-pointer'>
                            Show Both
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className='flex gap-4'>
                      <div className='flex items-center'>
                        <input
                          {...register('show_stats')}
                          id='show_stats'
                          type='checkbox'
                          className='h-4 w-4 mr-2 bg-transparent rounded-sm border-teal-800 text-teal-800 focus:ring-teal-500 cursor-pointer'
                        />
                        <label
                          htmlFor='show_stats'
                          className='block text-sm font-medium text-gray-700 cursor-pointer'>
                          Show Stats
                        </label>
                      </div>

                      <div className='flex items-center'>
                        <input
                          {...register('show_signals')}
                          id='show_signals'
                          type='checkbox'
                          className='h-4 w-4 mr-2 bg-transparent rounded-sm border-teal-800 text-teal-800 focus:ring-teal-500 cursor-pointer'
                        />
                        <label
                          htmlFor='show_signals'
                          className='block text-sm font-medium text-gray-700 cursor-pointer'>
                          Show Signals
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type='submit'
                  disabled={!isValid || isSubmitting}
                  className='btn btn-primary btn-sm mt-4 cursor-pointer w-32 mx-auto'>
                  {isSubmitting ? (
                    <AntIcon name='loading1' className={`!text-white animate-spin `} />
                  ) : (
                    'Submit'
                  )}
                </button>
              </form>
              {error && (
                <p className='text-red-500 text-sm mt-4 break-words whitespace-normal overflow-wrap-anywhere'>
                  {error}
                </p>
              )}
            </div>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}

ChartBuilderMenu.displayName = 'ChartBuilderMenu';

export default memo(ChartBuilderMenu);
